import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { OnboardingProvider } from './hooks/useOnboardingState.jsx';
import './OnboardingLayout.css';

const OnboardingLayout = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const userData = useAtomValue(userInfoAtom);

	useEffect(() => {
		const saved = sessionStorage.getItem('onboarding_state');
		const currentState = saved ? JSON.parse(saved) : {};

		const path = location.pathname;
		const search = location.search || '';
		const isCreateProfile = path.includes('/onboarding/create-profile');
		const isSelectPlan = path.includes('/onboarding/select-plan');
		const isCheckout = path.includes('/onboarding/checkout');
		const isWelcome = path.includes('/onboarding/welcome');
		const isStepPath = /\/onboarding\/step-\d+$/.test(path);
		const isStep5Or6 = path.includes('/onboarding/step-5') || path.includes('/onboarding/step-6');
		const isRecommended = path.includes('/onboarding/recommended-videos');
		const hasActiveSubscription = Boolean(
			userData?.subscriptionId || userData?.user?.subscriptionId || userData?.user?.subscription
		);
		const isOnboardingIncomplete = userData?.user?.onboardingCompleted === false;

		if (isCreateProfile) return;

		if (isSelectPlan && !currentState.profileCreated && userData.token) {
			sessionStorage.setItem(
				'onboarding_state',
				JSON.stringify({
					...currentState,
					profileCreated: true,
					currentStep: currentState.currentStep || 1
				})
			);
			return;
		}

		if (isSelectPlan && !currentState.profileCreated) {
			navigate(`/onboarding/create-profile${search}`, { replace: true });
			return;
		}

		if (isCheckout) {
			if (hasActiveSubscription && isOnboardingIncomplete) {
				navigate(`/onboarding/welcome${search}`, { replace: true });
				return;
			}
			if (!currentState.profileCreated && !userData.token) {
				navigate(`/onboarding/create-profile${search}`, { replace: true });
				return;
			}
			return;
		}

		if (
			isWelcome &&
			currentState.checkoutStatus !== 'completed' &&
			!(hasActiveSubscription && isOnboardingIncomplete)
		) {
			navigate(`/onboarding/checkout${search}`, { replace: true });
			return;
		}

		if (isStepPath) {
			if (!hasActiveSubscription && currentState.checkoutStatus !== 'completed') {
				navigate(`/onboarding/checkout${search}`, { replace: true });
				return;
			}
		}

		const planFromQuery = new URLSearchParams(search).get('plan');
		const isStudio =
			currentState.selectedPlanType === 'studio' ||
			currentState.userType === 'studio-owner' ||
			planFromQuery === 'studio';
		if (isStep5Or6 && !isStudio) {
			navigate(`/onboarding/recommended-videos${search}`, { replace: true });
			return;
		}

		if (isRecommended) {
			if (!hasActiveSubscription && currentState.checkoutStatus !== 'completed') {
				navigate(`/onboarding/checkout${search}`, { replace: true });
				return;
			}
		}
	}, [location.pathname, location.search, navigate]);

	return (
		<OnboardingProvider>
			<div className="onboarding-container">
				<Outlet />
				<ToastContainer />
			</div>
		</OnboardingProvider>
	);
};

export default OnboardingLayout;
